/**
 * The server's clock, estimated from the `Date` header of API answers. Due / overdue times on screen (ทักตาม due,
 * the follow-up window, Meta's 24 h) are the server's: a computer clock a minute off would move rows at the wrong
 * time. stores/clock.ts adds this offset to its "now". No Vue imports.
 *
 * Each answer bounds the offset: the server wrote `Date` (whole seconds, so [D, D + 1 s)) somewhere between our send
 * and our receipt → offset ∈ [D − received, D + 1 s − sent]. The newest answers that agree are intersected (a tight
 * estimate after a few calls); when an older one disagrees (a clock was changed) the newer ones win.
 */
interface Bound {
  lo: number
  hi: number
  at: number
}

const KEEP = 20
const KEEP_MS = 10 * 60_000

let bounds: Bound[] = []
let offset = 0

/** Server time minus this computer's time, in ms (0 until an answer with a Date header came). */
export function serverOffset(): number {
  return offset
}

/** An API answer arrived: its `Date` header, when we sent the request and when the answer came (Date.now()). */
export function noteServerDate(date: string | null | undefined, sentAt: number, gotAt: number): void {
  if (!date) return
  const d = Date.parse(date)
  if (!Number.isFinite(d) || gotAt < sentAt) return
  bounds.push({ lo: d - gotAt, hi: d + 1000 - sentAt, at: gotAt })
  bounds = bounds.filter((b) => gotAt - b.at <= KEEP_MS).slice(-KEEP)
  let lo = Number.NEGATIVE_INFINITY
  let hi = Number.POSITIVE_INFINITY
  for (let i = bounds.length - 1; i >= 0; i--) {
    const b = bounds[i]!
    const nlo = Math.max(lo, b.lo)
    const nhi = Math.min(hi, b.hi)
    if (nlo > nhi) break // an older answer disagrees: a clock changed since — the newer answers win
    lo = nlo
    hi = nhi
  }
  offset = Math.round((lo + hi) / 2)
}

/** Tests. */
export function resetServerTime(): void {
  bounds = []
  offset = 0
}
